import { useCallback, useMemo, useState } from 'react'
import { Copy, Tooltip, t } from '@stokr/components-library'
import type { CopyKey } from 'locales/copy-keys.gen'
import { InfoIcon } from 'components/sto/Section'
import { disclaimerTooltip } from 'components/ui/investmentCard/InvestmentCard'
import { AXIS_CHART_HEIGHT, ChartFootnote, RangePicker } from 'components/sto/chart'
import {
  ChartTabs,
  PriceChart,
  chartColor,
  formatChartAxisPercent,
  formatChartCompact,
  formatChartMoney,
  formatChartPercent,
} from 'components/ui/chart'
import { useChartRange } from 'hooks/sto/useChartRange'
import { latestDate, returnPoints, type ProductChartView } from 'models/productChart'
import { useChartDenomination } from 'hooks/sto/useProductChart'
import { fundViewPoints, type FundView, type ReturnStats } from 'models/chart'
import { DownloadCsv } from './PriceHistory'
import {
  ChartArea,
  ChartCard,
  ChartHeader,
  Controls,
  FundLegend,
  LegendDot,
  Stat,
  StatLabel,
  StatValue,
  Stats,
} from './Performance.styles'
import { useChartHoverTracking, useStoTrack } from 'hooks/sto/useStoTracking'

const VIEWS: Array<{ value: FundView; labelKey: CopyKey }> = [
  { value: 'cumulative', labelKey: 'stoPage.performance.view.cumulative' },
  { value: 'monthly', labelKey: 'stoPage.performance.view.monthly' },
  { value: 'nav', labelKey: 'stoPage.performance.view.nav' },
]

const STATS: Array<{ key: keyof ReturnStats; labelKey: CopyKey; infoKey: CopyKey }> = [
  {
    key: 'yearOverYear',
    labelKey: 'stoPage.performance.stats.yearOverYear',
    infoKey: 'stoPage.performance.stats.yearOverYearInfo',
  },
  {
    key: 'annualized',
    labelKey: 'stoPage.performance.stats.annualized',
    infoKey: 'stoPage.performance.stats.annualizedInfo',
  },
  {
    key: 'cumulative',
    labelKey: 'stoPage.performance.stats.cumulative',
    infoKey: 'stoPage.performance.stats.cumulativeInfo',
  },
]

/** Past this many monthly points the dots crowd the line. */
const MAX_MARKED_POINTS = 60

/**
 * Fund (Figma 3990:76993): the cumulative / monthly / NAV views share a row with the ranges,
 * then the legend, the chart, its source line with the CSV, and since-launch stats.
 */
export const FundPerformance = ({ chart, statsByCurrency, history }: ProductChartView) => {
  const [picked, setView] = useState<FundView>('cumulative')
  const trackSto = useStoTrack()
  const trackHover = useChartHoverTracking('performance')
  // The currency picked by the hero title (Goldstream: XAUt / USD), else the default.
  const denomination = useChartDenomination(chart) ?? chart.denominations[0]
  // The NAV view is the fund's size (unit NAV × units), in the picked currency only — never another
  // one; without it there is none to show, so no view.
  const fundNav = chart.fundNavs?.find((item) => item.currency.toLowerCase() === denomination.currency.toLowerCase())
  const hasFundNav = Boolean(fundNav)
  const views = hasFundNav ? VIEWS : VIEWS.filter((option) => option.value !== 'nav')
  const view = picked === 'nav' && !hasFundNav ? 'cumulative' : picked
  const fundNavPoints = view === 'nav' && fundNav ? fundNav.points : undefined
  // Returns (cumulative, monthly) on what they are measured on: Goldstream's published alpha in gold.
  const range = useChartRange(fundNavPoints ?? returnPoints(denomination))
  const points = useMemo(
    () => (fundNavPoints ? range.points : fundViewPoints(view, range.points)),
    [fundNavPoints, view, range.points],
  )
  const valueCurrency = fundNavPoints && fundNav ? fundNav.currency : denomination.currency
  const stats = statsByCurrency[denomination.currency] ?? {}
  const formatNav = useCallback((value: number) => formatChartMoney(value, valueCurrency), [valueCurrency])

  return (
    <ChartCard>
      <ChartArea>
        <ChartHeader>
          <Controls>
            <ChartTabs<FundView>
              wide
              options={views.map(({ value, labelKey }) => ({ value, label: t(labelKey) }))}
              value={view}
              onChange={(next) => {
                if (next !== view) trackSto('sto_performance_view_selected', { view: next })
                setView(next)
              }}
              label={t('stoPage.performance.viewLabel')}
              dataCy="sto-performance-view"
            />
          </Controls>
          <RangePicker range={range} chart="performance" placement="header" />
        </ChartHeader>

        <FundLegend>
          <LegendDot $color={chartColor.fund} aria-hidden="true" />
          <Copy
            id={fundNavPoints ? 'stoPage.performance.legendFundNav' : 'stoPage.performance.legendFund'}
            values={{ currency: valueCurrency }}
          />
        </FundLegend>

        <PriceChart
          kind={view === 'monthly' ? 'column' : 'area'}
          points={points}
          color={chartColor.fund}
          negativeColor={chartColor.negative}
          showMarkers={Boolean(fundNavPoints) && points.length <= MAX_MARKED_POINTS}
          height={AXIS_CHART_HEIGHT}
          showAxes
          onHover={trackHover}
          formatValue={view === 'nav' ? formatChartCompact : formatChartAxisPercent}
          formatTooltipValue={view === 'nav' ? formatNav : formatChartPercent}
          description={t(VIEWS.find((option) => option.value === view)?.labelKey ?? 'stoPage.performance.title')}
        />
        <RangePicker range={range} chart="performance" placement="below" />

        <ChartFootnote
          asOf={(fundNavPoints ? latestDate(fundNavPoints) : latestDate(denomination.points)) ?? chart.asOf}
          source={chart.source}>
          <DownloadCsv history={history} symbol={chart.symbol} />
        </ChartFootnote>
      </ChartArea>

      <Stats>
        {STATS.map(({ key, labelKey, infoKey }) => (
          <Stat key={key}>
            <StatLabel>
              <Copy id={labelKey} />
              {/* Returns are past performance: the disclaimer is always appended. */}
              <Tooltip
                placement="top"
                content={disclaimerTooltip(t(infoKey), {
                  tooltipDisclaimer: false,
                  tooltipPastPerformanceDisclaimer: true,
                })}>
                <InfoIcon role="button" tabIndex={0} aria-label={t('stoPage.keyInformation.moreInfo')} />
              </Tooltip>
            </StatLabel>
            <StatValue>{stats[key] === undefined ? '–' : formatChartPercent(stats[key])}</StatValue>
          </Stat>
        ))}
      </Stats>
    </ChartCard>
  )
}
